/**
 * `borde-sesion.ts` — el cable de las dos pantallas de la puerta contra
 * `auth.<sitio>` (`apps/identity-web`, `R2-IW1`).
 *
 * Etapa `R2-UI5` (`specs/R2-F6-identity-apps/etapas/r2-ui5.md`).
 * Contrato: `contracts/identity/http.ts` (`R2-CID1`, `ADR-0325`).
 *
 * ════════════════════════════════════════════════════════════════════════════
 * ⛔⛔ ⟦el-literal-a-mano-anclado-por-tipo⟧ POR QUÉ ACÁ HAY LITERALES
 * ════════════════════════════════════════════════════════════════════════════
 *
 * `r2-ui5.md` pide *«rutas y códigos **importados** de `IDENTITY_HTTP` /
 * `IDENTITY_STATUS`, nunca literales»*. **Los tres son VALORES, y esta app no
 * puede importar un valor de un contrato** — `vite.config.ts:10-16` lo dice y lo
 * hace cumplir la herramienta: *«un import de VALOR desde `contracts/` … hace
 * fallar el build»*. El `tsconfig` **sí** tiene el `paths`, así que el typecheck
 * pasaría y **el build no**: los dos instrumentos miran cosas distintas.
 *
 * ⇒ **`ADR-0330`**: el literal se escribe **a mano** y se **ANCLA con un alias de
 * TIPO** que el contrato publica. El bundler ve un string pelado —no resuelve
 * nada, no arrastra `better-auth` ni `kysely` al navegador— y **`tsc` rechaza el
 * literal equivocado**, porque cada alias es un tipo literal. Dos fuentes que no
 * pueden divergir, con cero imports de valor.
 *
 * Es la forma que el repo ya usaba y sancionó:
 * `configuracion-del-chatbot/borde-configuracion.ts:166`.
 *
 * ⚠️ **Si alguien cambia una ruta en el contrato, esto NO compila.** Es el punto.
 */

import type {
  ChooseInitialPasswordMethod,
  ChooseInitialPasswordPath,
  ChooseInitialPasswordRequestBody,
  GetSessionMethod,
  GetSessionPath,
  MustChoosePasswordStatus,
  NoSessionStatus,
  SessionResponseBody,
  SignInMethod,
  SignInPath,
  SignInRequestBody,
  SignOutMethod,
  SignOutPath,
} from "@contracts/identity/http.ts"
import type { MinPasswordLength } from "@contracts/identity/http.ts"
import { mensajeDeError, motivoDelCuerpo } from "../mensaje-de-error.ts"

/**
 * ⛔⛔ POR QUÉ EL RECHAZO USA `motivoDelCuerpo` Y NO `mensajeDeError`.
 *
 * `mensajeDeError` devuelve `«<motivo> · <genérico>»` —le PEGA DETRÁS «El
 * servidor respondió 401.»—, que es lo correcto para un error de operación: el
 * operador lee primero qué corregir y después el código.
 *
 * **Para el rechazo de login está mal.** `R2-F6-CA-07/08/20` piden que la
 * pantalla diga, por IGUALDAD, `SIGN_IN_REJECTED_COPY` — y nada más. El copy
 * correcto MÁS una cola es una de las formas de errarle que el examen de esta
 * etapa caza por nombre (`rechazo-con-cola`), y con razón: la cola le cuenta a
 * quien mira la pantalla que hubo un 401, que es justo lo que la Regla 5 no
 * quiere decir.
 */
function textoDelRechazo(respuesta: Response, generico: string): Promise<string> {
  return motivoDelCuerpo(respuesta).then((motivo) => motivo ?? generico)
}

// ════════════════════════════════════════════════════════════════════════════
// LAS RUTAS Y LOS CÓDIGOS · literal a mano + alias de tipo del contrato
// ════════════════════════════════════════════════════════════════════════════

const RUTA_ENTRAR: SignInPath = "api/auth/sign-in/email"
const METODO_ENTRAR: SignInMethod = "POST"

const RUTA_SALIR: SignOutPath = "api/auth/sign-out"
const METODO_SALIR: SignOutMethod = "POST"

const RUTA_QUIEN_SOY: GetSessionPath = "api/auth/get-session"
const METODO_QUIEN_SOY: GetSessionMethod = "GET"

const RUTA_ELEGIR: ChooseInitialPasswordPath = "api/auth/elegir-contrasena"
const METODO_ELEGIR: ChooseInitialPasswordMethod = "POST"

/** 401 — sin sesión válida. Los cinco casos que `resolverSesion` colapsa en `null`. */
const SIN_SESION: NoSessionStatus = 401
/** 409 — hay sesión, pero la marca está puesta. */
const DEBE_ELEGIR: MustChoosePasswordStatus = 409

/**
 * El mínimo de la Regla 7, en **puntos de código**.
 *
 * ⛔ El alias lo ancla al contrato: si `MIN_PASSWORD_LENGTH` cambia, esto no
 * compila. Y se cuenta con `[...clave].length` y **no** con `.length`, que cuenta
 * unidades UTF-16 y haría que la regla valiera sólo para el alfabeto latino.
 */
export const MINIMO_DE_CONTRASENA: MinPasswordLength = 8

/** Cuenta PUNTOS DE CÓDIGO, no unidades UTF-16. */
export function largoEnPuntosDeCodigo(texto: string): number {
  return [...texto].length
}

// ════════════════════════════════════════════════════════════════════════════
// EL BORDE
// ════════════════════════════════════════════════════════════════════════════

/** El actor que devuelve «quién soy», con la forma plana del contrato. */
export type ActorDeLaSesion = SessionResponseBody

export interface ResultadoDeEntrar {
  /** `true` si el servidor aceptó. */
  readonly entro: boolean
  /**
   * El texto a pintar cuando no entró. Es el que mandó el servidor
   * (`SIGN_IN_REJECTED_COPY`), no uno que esta unidad invente.
   */
  readonly mensaje: string | null
}

export interface BordeSesion {
  entrar(correo: string, contrasena: string): Promise<ResultadoDeEntrar>
  salir(): Promise<void>
  quienSoy(): Promise<ActorDeLaSesion | null>
  elegirContrasena(nueva: string): Promise<ResultadoDeEntrar>
}

/**
 * Error del borde con `statusCode`, como los otros diez bordes de la SPA
 * (molde: `proveedor-datos.ts:180`). Se reimplementa y no se importa porque allá
 * es privada — y `proveedor-datos.ts` es `ui-marco`, territorio de `R2-UI6`.
 */
function errorDelBorde(mensaje: string, statusCode: number): Error & { statusCode: number } {
  return Object.assign(new Error(mensaje), { statusCode })
}

/**
 * Arma el borde contra la raíz que le pasan.
 *
 * ⛔ **La raíz entra por parámetro y NO se lee de `configuracion.ts`.** Guard 1 de
 * `r2-ui5.md`: `configuracion.ts` es `ui-marco` y es `R2-UI6` quien lo extiende
 * con `identityWebUrl`. Leerlo acá volvería esta etapa dependiente de la única
 * wave exclusiva de la SPA, y dejaría de poder correr en paralelo con `R2-AW5`.
 */
export function crearBordeSesion(raiz: string): BordeSesion {
  /**
   * ⛔ `credentials: "include"` en los cuatro pedidos, y **ningún `Authorization`
   * armado acá**: la sesión viaja en una cookie `HttpOnly` que el navegador manda
   * solo. Un token que el JavaScript tuviera que leer sería un token que la
   * pantalla tuvo que GUARDAR, y esta unidad no guarda nada.
   *
   * ⚠️ Y `auth.<sitio>` es **otro subdominio**, así que sin `include` el navegador
   * no manda la cookie y todo contesta 401 — un modo de falla que se lee como
   * «la contraseña está mal».
   */
  const pedir = async (ruta: string, init: RequestInit): Promise<Response> => {
    try {
      return await fetch(`${raiz}/${ruta}`, { ...init, credentials: "include" })
    } catch (causa) {
      throw Object.assign(errorDelBorde(`No se pudo hablar con el servidor (${raiz}).`, 0), {
        cause: causa,
      })
    }
  }

  const conJson = (cuerpo: unknown): RequestInit => ({
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify(cuerpo),
  })

  return {
    /**
     * ⛔ **La contraseña viaja TAL COMO LA PERSONA LA ESCRIBIÓ.** RT-5: *«la
     * comparación es exacta … sin normalizar formatos»*. Nadie hace `trim()` acá
     * — ni la pantalla, ni el servidor. Quitarle los puntos al DNI «para ayudar»
     * haría `R2-F6-CA-10` imposible de cumplir.
     */
    async entrar(correo, contrasena) {
      const cuerpo: SignInRequestBody = { email: correo, password: contrasena }
      const respuesta = await pedir(RUTA_ENTRAR, { method: METODO_ENTRAR, ...conJson(cuerpo) })
      if (respuesta.ok) return { entro: true, mensaje: null }
      /**
       * ⛔ Los tres rechazos de la Regla 5 —correo desconocido, contraseña
       * incorrecta y cuenta dada de baja— llegan con el MISMO código y el MISMO
       * cuerpo. Esta unidad **no los distingue** y no agrega nada: distinguirlos
       * «con un texto más útil» le cuenta a alguien de afuera qué correos existen.
       */
      return {
        entro: false,
        mensaje: await textoDelRechazo(respuesta, `El servidor respondió ${respuesta.status}.`),
      }
    },

    async salir() {
      await pedir(RUTA_SALIR, { method: METODO_SALIR })
    },

    /**
     * `null` cuando no hay sesión (401). Cualquier otro fallo **se propaga**: «no
     * hay sesión» y «el borde se cayó» no se manejan igual, y colapsarlos en
     * `null` mandaría al login a alguien que sí tiene sesión.
     */
    async quienSoy() {
      const respuesta = await pedir(RUTA_QUIEN_SOY, { method: METODO_QUIEN_SOY, headers: { Accept: "application/json" } })
      if (respuesta.status === SIN_SESION) return null
      if (!respuesta.ok) {
        throw errorDelBorde(
          await mensajeDeError(respuesta, `El servidor respondió ${respuesta.status}.`),
          respuesta.status,
        )
      }
      return (await respuesta.json()) as ActorDeLaSesion
    },

    /**
     * ⛔ **UN SOLO CAMPO.** El contrato no tiene `currentPassword` ni «repetir»:
     * el segundo campo se compara **en la pantalla**. Mandarlo obligaría al
     * servidor a decidir qué hacer cuando los dos difieren, que es una pregunta
     * que el PRD no le hace.
     */
    async elegirContrasena(nueva) {
      const cuerpo: ChooseInitialPasswordRequestBody = { newPassword: nueva }
      const respuesta = await pedir(RUTA_ELEGIR, { method: METODO_ELEGIR, ...conJson(cuerpo) })
      if (respuesta.ok) return { entro: true, mensaje: null }
      return {
        entro: false,
        mensaje: await textoDelRechazo(respuesta, `El servidor respondió ${respuesta.status}.`),
      }
    },
  }
}

/** El 409 de «todavía no elegiste contraseña», para que la pantalla lo reconozca. */
export const ESTADO_DEBE_ELEGIR = DEBE_ELEGIR
/** El 401 de «sin sesión». */
export const ESTADO_SIN_SESION = SIN_SESION
